/* eslint-disable sonar/no-nested-conditional */
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Colors, MediaQueries, FontSizes, Spacing } from "@paclife/plds.tokens";
import titleCase from "utils/formatters/titleCase"

import ConfirmModal from "components/ConfirmModal/ConfirmModal";
import Card from "common/Card/Card";
import Typography from "common/Typography/Typography";
import Button from "common/Button/Button";
import { Icon } from "@paclife/plds.icon";

import { useTeamContext } from "pages/MyTeam/MyTeam";
import { removeFromTeam } from "api/team";


const Text = styled(Typography)`
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    &.Invited {
        color: ${Colors.primary};
    }
`;

const Grid = styled.div`
    width: 100%;
    margin-bottom: 12px;
    display: grid;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    @media (min-width: ${MediaQueries.md}) {
        grid-template-columns: 2.0fr 2.5fr .4fr;
    }
    @media (min-width: ${MediaQueries.lg}) {
        grid-template-columns: 2.0fr 2.5fr .4fr 20px;
    }
`;

const RowHeader = styled(Grid)`
    border-bottom: 1px solid ${Colors.neutral800};
`;

const Row = styled(Grid)`
    border-bottom: 1px solid ${Colors.neutral200};
`;

const MemberTools = styled.a`
    width: 100%;
    text-align: center;
`;

const TrashBin = styled(Icon)`
    width: 22px;
    cursor: pointer;
`;

const Notice = styled(Typography)`
    margin-bottom: ${Spacing[16]};
    font-size: ${FontSizes[400]};
    line-height: 1rem;
`;

const DISPLAY_FIELDS = [
    { label: "Name", name: "name" },
    { label: "Email", name: "emailAddress" },
    { label: "Remove", name: "remove" }
];

const REMOVE_MODAL = "remove";


const AssistantMyTeam = () => {
    const { t } = useTranslation("manMyTeam");
    const {
        membership,
        setMembership
    } = useTeamContext();
    
    const [mobileView, setMobileView] = useState(false);
    const [modal, setModal] = useState(false);
    const [memberSysId, setMemberSysId] = useState('');
    const [email, setEmail] = useState("");
    const [memberDisplay, setMemberDisplay] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const { rsdUpReadonlyaccessEn } = useFlags();


    useEffect(() => {
        function updateView() {
            if (window.innerWidth <= parseInt(MediaQueries.md)) {
                setMobileView(true);
            } else {
                setMobileView(false);
            }
        }
        window.addEventListener("resize", updateView);
        updateView();
        return () => window.removeEventListener("resize", updateView);
    }, []);

    useEffect(() => {
        if (membership.length > 0) {
            setMemberDisplay(true)
            setMemberSysId(membership[0].id)       
        }
    }, [membership]);

    const openModal = (modal, memberId, email) => {
        setModal(modal);
        setMemberSysId(memberId);
        setEmail(email);
    };

    const closeModal = () => {
        setModal(false);
        setMemberSysId("");
        setEmail("");
        setError("");
    };

    const handleClick = async (e) => {
        if (rsdUpReadonlyaccessEn) {
            return;
        } else {
            handleRemove(e);
        }
    };

    const handleRemove = async (e) => {
        setLoading(true);
        const { succeed, message } = await removeFromTeam(memberSysId);

        if (succeed) {
            setMembership((prev) => {
                return prev.filter((member) => member.id !== memberSysId);
            });
            closeModal();
        } else {
            setError(message);
        }
        setLoading(false);
    };

    const memberTools = (memId) => {

        return (
            <MemberTools onClick={() =>
                openModal(
                    REMOVE_MODAL,
                    memId
                )
            }
                data-track="analytics"
                data-bname="remove-access"
                disabled={rsdUpReadonlyaccessEn}
                title="Remove Access"
            >
                <TrashBin 
                    name="delete" 
                    color={Colors.red700}
                />
            </MemberTools>
        )
    }

    return (
        <>
            { memberDisplay && (
                <Card title={t("membership.title")}>
                    <Notice>
                        {t("membership.message")}
                    </Notice>
                    <div>
                        {!mobileView && (
                            <RowHeader>
                                {DISPLAY_FIELDS.map((field) => (
                                    <Text key={field.label} variant="caption">
                                        {field.label}
                                    </Text>
                                ))}
                            </RowHeader>
                        )}
                        {( membership.map((member, index) => (
                                <Row key={index}>
                                    {DISPLAY_FIELDS.map((field) => (
                                        <Text
                                            variant="body"
                                            className={member[field.name]}
                                        >
                                            {mobileView
                                                ? `${field.label}: ${
                                                    member[field.name] || "-"
                                                }`
                                                : titleCase(member[field.name]) || 
                                                    memberTools(
                                                        member['id']
                                                    )
                                            }
                                        </Text>
                                    ))}
                                </Row>
                            ))
                        )}
                    </div>
                </Card>
            )}
            {modal && (
                <ConfirmModal
                    disableOutsideClick
                    title={t(`manage.${modal}.title`)}
                    subtitle={t(`manage.${modal}.subtitle`, { value: email })}
                    closeModal={closeModal}
                    primaryButton={
                        <Button
                            variant="primary"
                            onClick={handleClick}
                            data-track="analytics"
                            data-bname={`confirm-${modal}`}
                            disabled={rsdUpReadonlyaccessEn}
                        >
                            {t(`manage.${modal}.primaryButton`)}
                        </Button>
                    }
                    secondaryButton={
                        <Button
                            variant="secondaryNeutral"
                            onClick={closeModal}
                            data-track="analytics"
                            data-bname={`cancel-${modal}`}
                        >
                            {t(`manage.${modal}.secondaryButton`)}
                        </Button>
                    }
                    error={error}
                    loading={loading}
                />
            )}
        </>
    );
};

export default AssistantMyTeam;